/**
 * pages/milestone-create.tsx
 *
 * Form to create a new milestone.
 * - Title input (required)
 * - Date input (required)
 * - Note textarea (optional)
 * - Multiple photo picker with previews (optional)
 * - Save button (disabled until title + date filled)
 * - Submits as multipart/form-data via useCreateMilestone
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateMilestone } from '../hooks/use-milestones'

interface PhotoPreview {
  file: File
  url: string
}

export default function MilestoneCreatePage() {
  const navigate = useNavigate()
  const createMilestone = useCreateMilestone()

  const [title, setTitle] = useState('')
  const [date, setDate] = useState('')
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<PhotoPreview[]>([])
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const canSave = title.trim().length > 0 && date.length > 0 && !createMilestone.isPending

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files ?? [])
    if (newFiles.length === 0) return

    const previews = newFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }))

    setPhotos((prev) => [...prev, ...previews])

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => {
      const removed = prev[index]
      if (removed) URL.revokeObjectURL(removed.url)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleSave = async () => {
    if (!canSave) return
    setError(null)

    const formData = new FormData()
    formData.append('title', title.trim())
    formData.append('date', date)
    if (note.trim()) formData.append('note', note.trim())
    for (const p of photos) formData.append('files', p.file)

    try {
      await createMilestone.mutateAsync(formData)
      navigate('/milestones')
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save milestone')
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            New Milestone
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-[34px] px-4 rounded-full bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-95 active:bg-[#993556] transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:shadow-none disabled:active:scale-100"
          >
            {createMilestone.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {/* Form */}
      <main className="flex-1 overflow-y-auto px-5 pt-5 pb-10 flex flex-col gap-4 animate-fade-in-up">
        {/* Error banner */}
        {error && (
          <div className="bg-[#FFF0F4] border border-[#F4C0D1] rounded-[12px] px-4 py-3">
            <p className="font-['DM_Sans'] text-[13px] text-[#993556]">{error}</p>
          </div>
        )}

        {/* Title */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="milestone-title"
          >
            Title
          </label>
          <input
            id="milestone-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Our first trip together"
            maxLength={200}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] placeholder-[#C4A9B2] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Date */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="milestone-date"
          >
            Date
          </label>
          <input
            id="milestone-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Note */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="milestone-note"
          >
            Note <span className="font-normal normal-case text-[#B89AA2]">(optional)</span>
          </label>
          <textarea
            id="milestone-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A few words about this moment…"
            maxLength={1000}
            rows={4}
            className="w-full px-4 py-3 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] placeholder-[#C4A9B2] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150 resize-none leading-relaxed"
          />
        </div>

        {/* Photos */}
        <div className="flex flex-col gap-2">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="milestone-photos"
          >
            Photos <span className="font-normal normal-case text-[#B89AA2]">(optional)</span>
          </label>

          {photos.length > 0 ? (
            <div className="flex flex-col gap-3">
              {/* Photo grid */}
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo, i) => (
                  <div
                    key={photo.url}
                    className="relative rounded-[12px] overflow-hidden border border-[#F4C0D1] aspect-square bg-[#FAF8F4]"
                  >
                    <img
                      src={photo.url}
                      alt={`Moment ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(i)}
                      className="
                        absolute top-1 right-1
                        w-[24px] h-[24px] rounded-full
                        bg-black/50 flex items-center justify-center
                        text-white text-[14px]
                        active:scale-90 transition-all duration-150
                        touch-manipulation
                      "
                      aria-label={`Remove photo ${i + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {/* Add more button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="
                  w-full h-[48px] rounded-[12px]
                  bg-white border-2 border-dashed border-[#F4C0D1]
                  font-['DM_Sans'] text-[14px] text-[#B89AA2]
                  flex items-center justify-center gap-2
                  active:bg-[#FBEAF0] transition-all duration-150
                  touch-manipulation
                "
              >
                <span>📷</span>
                <span>Add more photos</span>
              </button>

              <input
                id="milestone-photos"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="
                w-full h-[56px] rounded-[12px]
                bg-white border-2 border-dashed border-[#F4C0D1]
                font-['DM_Sans'] text-[14px] text-[#B89AA2]
                flex items-center justify-center gap-2
                active:bg-[#FBEAF0] transition-all duration-150
                touch-manipulation
              "
            >
              <span>📷</span>
              <span>Add photos</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
      </main>
    </div>
  )
}
